import { AuthLayout } from '@/components/auth'
import { Button, Control, EmailInput, useForm, Icon } from '@tempots/beatui'
import { attr, html, on, Ensure } from '@tempots/dom'
import { Validation } from '@tempots/std'
import { Anchor, NavigationService } from '@tempots/ui'
import { auth } from '@/services/gateway'
import { forgotPasswordSchema } from '@/models/auth'
import { isApiError } from '@/services/errors'

export function ForgotPasswordPage() {
  const { controller, submit, submitting } = useForm({
    initialValue: { email: '' },
    schema: forgotPasswordSchema,
    onSubmit: async (value) => {
      try {
        await auth.forgotPassword({ email: value.email })
        NavigationService.navigate(
          `/verify-email-sent?email=${encodeURIComponent(value.email)}&type=reset`
        )
        return Validation.valid
      } catch (err) {
        const message = isApiError(err)
          ? (err.detail ?? err.title)
          : err instanceof Error
            ? err.message
            : 'Failed to send reset link. Please try again.'
        return Validation.invalid({ message })
      }
    },
  })

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full space-y-8'),
      // Header
      html.div(
        attr.class('text-center'),
        html.h2(
          attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
          'Reset your password'
        ),
        html.p(
          attr.class('mt-2 text-sm sm:text-base text-gray-600'),
          "Enter your email and we'll send you a reset link"
        )
      ),
      // Error message display
      Ensure(controller.error, (error) =>
        html.div(
          attr.class(
            'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
          ),
          error
        )
      ),
      // Form
      html.form(
        on.submit((e) => {
          e.preventDefault()
          submit(e)
        }),
        attr.class('space-y-6'),
        html.div(
          Control(EmailInput, {
            controller: controller.field('email'),
            label: 'Email Address',
            placeholder: 'you@company.com',
          })
        ),
        html.div(
          attr.class('flex items-center justify-center'),
          Button(
            {
              type: 'submit',
              fullWidth: true,
              loading: submitting,
              disabled: submitting,
            },
            submitting.map((s) => (s ? 'Sending...' : 'Send reset link'))
          )
        )
      ),
      // Back to sign in
      html.div(
        attr.class('text-center'),
        Anchor(
          { href: '/signin' },
          attr.class(
            'inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700'
          ),
          Icon({ icon: 'mdi:arrow-left' }, attr.class('w-4 h-4')),
          'Back to sign in'
        )
      )
    )
  )
}
